import { ArrowRight, ArrowDown, PackageCheck, PackageOpen, Pencil, DoorOpen, Presentation, QrCode } from "lucide-react";

// Swappable Setup Diagram: renders a built-in spatial visual now, OR an
// uploaded image later (set diagram.mode === "image" and diagram.imageUrl).
// Keeping the render behind this single component means the image swap is a
// one-line data change with no layout rework.
export default function SetupDiagram({ diagram }) {
  if (!diagram) return null;
  return (
    <figure className="rounded-2xl border-2 border-dashed border-brand-orange/40 bg-white overflow-hidden">
      <figcaption className="flex items-center gap-2 bg-brand-orange/10 px-4 py-2.5 border-b border-brand-orange/20">
        <Presentation className="w-4 h-4 text-brand-orange" />
        <span className="text-xs font-bold uppercase tracking-wide text-brand-orange">Setup Diagram</span>
      </figcaption>
      <div className="p-4 sm:p-5">
        {diagram.mode === "image" && diagram.imageUrl ? (
          <img src={diagram.imageUrl} alt={diagram.caption} className="w-full rounded-xl" />
        ) : diagram.type === "drop-in-line" ? (
          <DropInLine stations={diagram.stations} />
        ) : diagram.type === "pickup-dropoff" ? (
          <PickupDropoff stations={diagram.stations} />
        ) : diagram.type === "self-hosted-pairs" ? (
          <SelfHostedPairs diagram={diagram} />
        ) : (
          <SelfHostedRoom diagram={diagram} />
        )}
        <p className="mt-4 text-sm text-brand-navy/70 italic text-center">{diagram.caption}</p>
      </div>
    </figure>
  );
}

function StationBox({ station }) {
  const isEnd = station.isEnd;
  const isCraft = station.craft;
  return (
    <div
      className={`rounded-xl border p-3 h-full ${
        isEnd
          ? "bg-brand-navy text-white border-brand-navy"
          : isCraft
          ? "bg-brand-cream border-brand-orange/40"
          : "bg-white border-brand-navy/15"
      }`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {isEnd ? <PackageCheck className="w-4 h-4" /> : isCraft ? <Pencil className="w-4 h-4 text-brand-orange" /> : null}
        <span className={`text-[11px] font-bold uppercase tracking-wide ${isEnd ? "text-white/70" : "text-brand-navy/50"}`}>
          {station.label}
        </span>
      </div>
      <p className={`text-sm font-semibold leading-tight ${isEnd ? "text-white" : "text-brand-navy"}`}>{station.title}</p>
      {station.pick && (
        <p className={`mt-1 text-xs font-medium ${isEnd ? "text-brand-orange" : "text-brand-orange"}`}>{station.pick}</p>
      )}
      {station.detail && (
        <p className={`mt-1 text-[11px] leading-snug ${isEnd ? "text-white/70" : "text-brand-navy/60"}`}>{station.detail}</p>
      )}
    </div>
  );
}

function DropInLine({ stations }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 text-xs font-semibold text-brand-navy/60">
        <span className="inline-flex items-center gap-1 rounded-full bg-brand-orange/10 text-brand-orange px-2.5 py-1">
          Start <ArrowRight className="w-3.5 h-3.5" />
        </span>
        <span className="h-px flex-1 bg-brand-navy/10" />
        <span className="inline-flex items-center gap-1 rounded-full bg-brand-navy/10 px-2.5 py-1">Finish</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stations.map((s, i) => (
          <StationBox key={i} station={s} />
        ))}
      </div>
    </div>
  );
}

function PickupDropoff({ stations }) {
  const pickup = stations.find((s) => s.role === "pickup") || stations[0];
  const dropoff = stations.find((s) => s.role === "dropoff") || stations[1];
  return (
    <div className="flex flex-col sm:flex-row items-stretch gap-3">
      <div className="flex-1 rounded-xl bg-brand-cream border border-brand-orange/40 p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-orange text-white flex items-center justify-center">
            <PackageOpen className="w-4 h-4" />
          </span>
          <div>
            {pickup.label && (
              <span className="block text-[10px] font-bold uppercase tracking-wide text-brand-navy/50">{pickup.label}</span>
            )}
            <p className="display-title text-base text-brand-navy leading-tight">{pickup.title}</p>
          </div>
        </div>
        {pickup.pick && <p className="text-sm font-semibold text-brand-orange">{pickup.pick}</p>}
        {pickup.detail && <p className="mt-0.5 text-[13px] leading-snug text-brand-navy/75">{pickup.detail}</p>}
        {pickup.note && (
          <p className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-brand-navy bg-white/70 rounded-md px-2 py-1">
            <QrCode className="w-3.5 h-3.5 text-brand-orange" /> {pickup.note}
          </p>
        )}
      </div>

      <div className="flex sm:flex-col items-center justify-center text-brand-navy/40">
        <ArrowRight className="w-6 h-6 hidden sm:block" />
        <ArrowDown className="w-6 h-6 sm:hidden" />
      </div>

      <div className="flex-1 rounded-xl bg-brand-navy text-white p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/15 text-white flex items-center justify-center">
            <PackageCheck className="w-4 h-4" />
          </span>
          <div>
            {dropoff.label && (
              <span className="block text-[10px] font-bold uppercase tracking-wide text-white/50">{dropoff.label}</span>
            )}
            <p className="display-title text-base leading-tight">{dropoff.title}</p>
          </div>
        </div>
        {dropoff.pick && <p className="text-sm font-semibold text-brand-orange">{dropoff.pick}</p>}
        {dropoff.detail && <p className="mt-0.5 text-[13px] leading-snug text-white/80">{dropoff.detail}</p>}
      </div>
    </div>
  );
}

function SelfHostedPairs({ diagram }) {
  return (
    <div>
      {/* Front of room */}
      <div className="rounded-xl bg-brand-orange text-white p-3 text-center mb-3">
        <p className="text-[11px] font-bold uppercase tracking-wide text-white/80">Front of room</p>
        <p className="text-sm font-semibold flex items-center justify-center gap-1.5">
          <Presentation className="w-4 h-4" /> {diagram.front.title}
        </p>
      </div>
      {/* Pair tables */}
      <div className="grid gap-3 sm:grid-cols-3">
        {diagram.tables.map((t, i) => (
          <div key={i} className="rounded-xl bg-brand-cream border border-brand-cream-dark p-3 text-center">
            <p className="text-[11px] font-bold uppercase tracking-wide text-brand-navy/50">{t.label}</p>
            <p className="text-sm font-semibold text-brand-navy">{t.pair}</p>
            {diagram.seatNote && <p className="mt-1 text-[11px] leading-snug text-brand-navy/60">{diagram.seatNote}</p>}
          </div>
        ))}
      </div>
      {/* Back of room */}
      <div className="mt-3 rounded-xl bg-brand-navy text-white p-3 flex items-center gap-2">
        <PackageCheck className="w-4 h-4" />
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-white/50">Back of room</p>
          <p className="text-sm font-semibold">{diagram.back.title}</p>
          {diagram.back.detail && <p className="text-[11px] text-white/70">{diagram.back.detail}</p>}
        </div>
      </div>
    </div>
  );
}

function SelfHostedRoom({ diagram }) {
  const tables = Array.from({ length: diagram.teamTables.count });
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_240px]">
      <div>
        {/* Ambassador front */}
        <div className="rounded-xl bg-brand-orange text-white p-3 text-center mb-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-white/80">Front of room</p>
          <p className="text-sm font-semibold flex items-center justify-center gap-1.5">
            <Presentation className="w-4 h-4" /> {diagram.front.title}
          </p>
          <p className="text-[11px] text-white/80">{diagram.front.detail}</p>
        </div>
        {/* Team tables */}
        <div className="grid grid-cols-2 gap-3">
          {tables.map((_, i) => (
            <div key={i} className="rounded-xl bg-brand-cream border border-brand-cream-dark p-3 text-center">
              <p className="text-sm font-semibold text-brand-navy">Team table {i + 1}</p>
              <p className="text-[11px] text-brand-navy/60">{diagram.teamTables.seatsEach}</p>
            </div>
          ))}
        </div>
        {/* Door area */}
        <div className="mt-3 rounded-xl bg-brand-navy text-white p-3 flex items-center gap-2">
          <DoorOpen className="w-4 h-4" />
          <div>
            <p className="text-sm font-semibold">{diagram.doorArea.title}</p>
            <p className="text-[11px] text-white/70">{diagram.doorArea.detail}</p>
          </div>
        </div>
      </div>
      {/* Supply table */}
      <div className="rounded-xl border border-brand-navy/15 bg-white p-3">
        <p className="text-[11px] font-bold uppercase tracking-wide text-brand-navy/50 mb-2">{diagram.supplyTable.title}</p>
        <ol className="space-y-2">
          {diagram.supplyTable.steps.map((s, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-orange text-white text-[10px] font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <span className="text-[13px] text-brand-navy">{s}</span>
              {i < diagram.supplyTable.steps.length - 1 && <ArrowDown className="w-3 h-3 text-brand-navy/30 ml-auto" />}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
